import React from 'react';

export default function BenefitsSection() {
  const benefits = [
    {
      icon: 'ri-flashlight-line',
      title: 'Enhanced Energy',
      description: 'Feel more energetic throughout the day with improved oxygen delivery to your cells.',
      image: 'https://static.readdy.ai/image/9842b5143901a0019f067b22648287f2/234fc59197c6a10fab795aec41524193.png',
      gradient: 'from-orange-400 to-red-600'
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Better Recovery',
      description: 'Accelerate muscle recovery and reduce fatigue after physical activities and workouts.',
      image: 'https://static.readdy.ai/image/9842b5143901a0019f067b22648287f2/dce8faaa7d242512e13261e1f9a4f0c1.png',
      gradient: 'from-blue-400 to-blue-600'
    },
    {
      icon: 'ri-contrast-2-line',
      title: 'Skin Glow',
      description: 'Achieve radiant, healthy-looking skin with enhanced oxygen circulation and cellular regeneration.',
      image: 'https://static.readdy.ai/image/9842b5143901a0019f067b22648287f2/5e0a1d6dceedde7f4250be3a08dc21f1.png',
      gradient: 'from-pink-400 to-rose-600'
    },
    {
      icon: 'ri-brain-line',
      title: 'Mental Clarity',
      description: 'Experience improved focus, concentration, and cognitive function with better brain oxygenation.',
      image: 'https://public.readdy.ai/ai/img_res/fa4567a4f292423dfd369aa7eeca031f.jpg',
      gradient: 'from-purple-400 to-indigo-600'
    }
  ];

  return (
    <section className="py-20 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#111111] mb-6">
            Transform Your Health with
            <span className="text-[#12B6B6]"> Oxygen Power</span>
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed">
            Experience the comprehensive benefits of our oxygen supplementation products, 
            designed to enhance your overall wellness from the inside out.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {benefits.map((benefit, index) => (
            <div 
              key={index}
              className="group bg-white rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-500 hover:-translate-y-2"
            >
              {/* Image */}
              <div className="aspect-[3/2] overflow-hidden relative">
                <img 
                  src={benefit.image} 
                  alt={benefit.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className={`absolute inset-0 bg-gradient-to-br ${benefit.gradient} opacity-20 group-hover:opacity-30 transition-opacity duration-300`}></div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`w-12 h-12 flex items-center justify-center rounded-2xl bg-gradient-to-br ${benefit.gradient} text-white`}>
                    <i className={`${benefit.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#111111]">{benefit.title}</h3>
                </div>

                <p className="text-gray-600 mb-4 leading-relaxed text-sm">{benefit.description}</p>

                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold text-[#12B6B6]">{benefit.stats}</div>
                  <div className="text-xs text-gray-500">Based on user studies</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Testimonial Section */}
        <div className="bg-white rounded-3xl p-8 md:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center space-x-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-xl"></i>
                ))}
                <span className="ml-2 text-sm text-gray-600">(2,847 reviews)</span>
              </div>
              
              <blockquote className="text-xl text-gray-700 mb-6 leading-relaxed italic">
                "I've been using OxyKits for my family for 6 months now. The energy boost is incredible, 
                and my skin has never looked better. The best part is how affordable it is through our local PHC."
              </blockquote>
              
              <div className="flex items-center space-x-4">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20satisfied%20Indian%20family%20mother%20in%20her%2040s%20smiling%20confidently%2C%20happy%20customer%20testimonial%20photo%2C%20healthcare%20product%20user%20showing%20satisfaction%20and%20wellness%2C%20natural%20lighting%20with%20warm%20expression&width=60&height=60&seq=customer-testimonial&orientation=squarish"
                  alt="Customer"
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <div className="font-semibold text-[#111111]">Priya Sharma</div>
                  <div className="text-sm text-gray-600">Mother of 3, Delhi</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#12B6B6]/5 rounded-2xl p-6 text-center">
                <div className="text-3xl font-bold text-[#12B6B6] mb-2">15K+</div>
                <div className="text-sm text-gray-600">Happy Customers</div>
              </div>
              <div className="bg-[#12B6B6]/5 rounded-2xl p-6 text-center">
                <div className="text-3xl font-bold text-[#12B6B6] mb-2">4.8</div>
                <div className="text-sm text-gray-600">Average Rating</div>
              </div>
              <div className="bg-[#12B6B6]/5 rounded-2xl p-6 text-center">
                <div className="text-3xl font-bold text-[#12B6B6] mb-2">89%</div>
                <div className="text-sm text-gray-600">Repeat Customers</div>
              </div>
              <div className="bg-[#12B6B6]/5 rounded-2xl p-6 text-center">
                <div className="text-3xl font-bold text-[#12B6B6] mb-2">30</div>
                <div className="text-sm text-gray-600">Day Guarantee</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
